import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PortalSidebar from '../components/PortalSidebar';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const STUDY_CHECKLIST = [
  { key: 'passport', label: 'Passport Copy', icon: 'badge', note: 'Required for all destinations' },
  { key: 'academicCertificates', label: 'Academic Certificates', icon: 'school', note: '' },
  { key: 'transcripts', label: 'Academic Transcripts', icon: 'history_edu', note: '' },
  { key: 'cv', label: "Curriculum Vitae (CV)", icon: 'description', note: "Master's applicants only" },
  { key: 'personalStatement', label: 'Statement of Purpose (SOP)', icon: 'contract', note: 'Personal essay for university admission' },
  { key: 'englishTestResults', label: 'Language Proficiency (IELTS/PTE)', icon: 'language', note: 'Required for UK/AU destinations' },
];

const JOB_CHECKLIST = [
  { key: 'passport', label: 'Passport Copy', icon: 'badge', note: '' },
  { key: 'cv', label: 'CV/Resume', icon: 'description', note: '' },
  { key: 'certificate', label: 'Academic/High School Certificate', icon: 'school', note: '' },
  { key: 'tradeCertifications', label: 'Trade Certifications', icon: 'construction', note: 'If applicable' },
  { key: 'photo', label: 'Passport-size Photo', icon: 'account_circle', note: '' },
];

export default function DocumentUploadPage() {
  const { id } = useParams();
  const [application, setApplication] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [viewingDocId, setViewingDocId] = useState(null);

  const fetchApplication = () => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/portal/applications/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => setApplication(json.success ? json.data : null));
  };

  useEffect(fetchApplication, [id]);

  const checklist = application?.type === 'JOB' ? JOB_CHECKLIST : STUDY_CHECKLIST;
  const uploadedFor = (label) =>
    (application?.documents || []).find((d) => d.documentType === label);

  const completedCount = checklist.filter((item) => uploadedFor(item.label)).length;

  const handleUpload = async (label, file) => {
    if (!file) return;
    setUploading(label);
    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('documentType', label);
    fd.append('file', file);
    await fetch(`${API_BASE}/applications/${id}/documents`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    setUploading(null);
    fetchApplication();
  };

  // Documents are private (uploaded as Cloudinary 'authenticated' assets) —
  // there's no static viewable link, so a fresh short-lived signed URL is
  // fetched on demand each time "View" is clicked.
  const handleView = async (doc) => {
    setViewingDocId(doc.id);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE}/applications/${id}/documents/${doc.id}/url`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) {
        window.open(json.data.url, '_blank', 'noopener,noreferrer');
      } else {
        alert(json.error?.message || 'Could not open this document.');
      }
    } catch {
      alert('Could not open this document — check your connection and try again.');
    } finally {
      setViewingDocId(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    const firstMissing = checklist.find((item) => !uploadedFor(item.label));
    if (file && firstMissing) handleUpload(firstMissing.label, file);
  };

  if (!application) return <p className="text-center py-40 text-[#64748b]">Loading...</p>;

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <PortalSidebar />
      <main className="md:ml-64 pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-[#00255a] mb-2">Upload Application Files</h3>
            <p className="text-[#44474f]">
              Please provide clear, scanned copies of the required documents to move your application to the verification stage.
            </p>
          </div>

          {/* Drop Zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('bulk-upload-input').click()}
            className={`relative cursor-pointer bg-white border-2 border-dashed rounded-2xl p-10 text-center transition-all mb-10 ${
              dragActive ? 'border-[#365da3] bg-[#365da3]/5 scale-[1.01]' : 'border-[#e2e8f0] hover:border-[#00255a]'
            }`}
          >
            <input
              id="bulk-upload-input"
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              onChange={(e) => {
                const firstMissing = checklist.find((item) => !uploadedFor(item.label));
                if (firstMissing) handleUpload(firstMissing.label, e.target.files[0]);
              }}
            />
            <div className="w-20 h-20 bg-[#00255a]/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="material-symbols-outlined text-[#00255a] text-4xl">cloud_upload</span>
            </div>
            <h4 className="text-xl font-bold text-[#00255a] mb-2">Drag and drop your files here</h4>
            <p className="text-[#64748b] mb-6">Or click to browse from your computer</p>
            <div className="flex flex-wrap justify-center gap-4 text-[13px]">
              <span className="flex items-center px-4 py-1 bg-[#eceef0] text-[#44474f] rounded-full border border-[#e2e8f0] font-medium">
                <span className="material-symbols-outlined text-[16px] mr-1">check_circle</span> PDF, JPG, JPEG, PNG, WEBP
              </span>
              <span className="flex items-center px-4 py-1 bg-[#eceef0] text-[#44474f] rounded-full border border-[#e2e8f0] font-medium">
                <span className="material-symbols-outlined text-[16px] mr-1">history</span> Max 10MB per file
              </span>
            </div>
          </div>

          {/* Checklist */}
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-lg font-bold text-[#191c1e]">Required Documents</h5>
              <span className="text-sm text-[#747781] font-semibold">{completedCount} OF {checklist.length} COMPLETED</span>
            </div>

            {checklist.map((item) => {
              const doc = uploadedFor(item.label);
              const status = doc ? doc.status : 'NOT_UPLOADED';
              const isUploading = uploading === item.label;

              const statusStyles = {
                VERIFIED: { bg: 'bg-[#ff8483]/10', text: 'text-[#862026]', icon: 'verified_user', label: 'Verified' },
                UPLOADED: { bg: 'bg-[#365da3]/10', text: 'text-[#365da3]', icon: 'pending', label: 'In Review' },
                REJECTED: { bg: 'bg-red-100', text: 'text-red-700', icon: 'error', label: 'Rejected' },
                NOT_UPLOADED: { bg: 'bg-[#747781]/10', text: 'text-[#747781]', icon: 'description', label: 'Not Uploaded' },
              }[status];

              return (
                <div
                  key={item.key}
                  className={`rounded-xl p-4 md:p-6 border border-[#e2e8f0] flex flex-col md:flex-row md:items-center gap-4 transition-all ${
                    status === 'NOT_UPLOADED' ? 'bg-[#f2f4f6]/50 opacity-80 hover:opacity-100' : 'bg-white shadow-sm hover:shadow-md'
                  }`}
                >
                  <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${statusStyles.bg} flex items-center justify-center`}>
                    <span className={`material-symbols-outlined ${statusStyles.text} text-[28px]`}>{statusStyles.icon}</span>
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h6 className="text-lg text-[#00255a] font-semibold">{item.label}</h6>
                      <span className={`px-2 py-0.5 ${statusStyles.bg} ${statusStyles.text} text-[11px] font-bold rounded uppercase tracking-wider`}>
                        {statusStyles.label}
                      </span>
                    </div>
                    <p className={`text-sm ${doc ? 'text-[#44474f] font-medium' : 'text-[#747781] italic'}`}>
                      {doc ? doc.fileName : item.note || 'No file attached yet'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {doc && (
                      <button
                        onClick={() => handleView(doc)}
                        disabled={viewingDocId === doc.id}
                        className="flex items-center text-[#00255a] font-semibold px-4 py-2 hover:bg-[#00255a]/5 rounded-lg text-sm disabled:opacity-60"
                      >
                        <span className="material-symbols-outlined text-[20px] mr-2">visibility</span>
                        {viewingDocId === doc.id ? 'Opening...' : 'View'}
                      </button>
                    )}
                    <label className={`flex items-center font-semibold px-6 py-2 rounded-lg text-sm cursor-pointer transition-colors ${
                      doc ? 'border-2 border-[#00255a] text-[#00255a] hover:bg-[#00255a]/5' : 'bg-[#00255a] text-white hover:scale-105'
                    }`}>
                      {isUploading ? 'Uploading...' : doc ? 'Replace' : 'Upload'}
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        disabled={isUploading}
                        onChange={(e) => handleUpload(item.label, e.target.files[0])}
                      />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer CTA */}
          <div
            className="mt-12 p-8 rounded-2xl text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
          >
            <div className="relative z-10 text-center md:text-left">
              <h4 className="text-xl font-bold mb-2">Ready to Submit?</h4>
              <p className="text-white/80 max-w-md">Once all required documents are verified, your application moves to the submission stage.</p>
            </div>
            <button
              disabled={completedCount < checklist.length}
              className="relative z-10 px-8 py-4 bg-white text-[#00255a] font-bold rounded-xl shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:hover:scale-100"
            >
              {completedCount < checklist.length ? `${checklist.length - completedCount} Remaining` : 'All Documents Uploaded'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}